import { useParams, Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { getMangaById, mangaList } from '@/mocks/manga';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'instant' });
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const manga = getMangaById(id || '');

  if (!manga) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-900">未找到该漫画</h1>
          <p className="mt-2 text-sm text-foreground-500">该漫画可能已被移除或链接有误</p>
          <Link to="/" className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedManga = mangaList
    .filter((m) => m.categorySlug === manga.categorySlug && m.id !== manga.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${manga.title} - 韩国漫画网`,
    'description': manga.description,
    'image': manga.coverImage,
    'author': {
      '@type': 'Person',
      'name': manga.author,
    },
    'datePublished': '2026-01-15',
    'dateModified': manga.updatedAt,
    'publisher': {
      '@type': 'Organization',
      'name': '韩国漫画网',
      'url': 'https://www.koreanmanhwa.cn/',
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://www.koreanmanhwa.cn/article/${manga.id}`,
    },
  };

  return (
    <div ref={scrollRef}>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="bg-background-100/70 pt-20 pb-0">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${manga.category}漫画`, href: `/category/${manga.categorySlug}` },
              { label: manga.title },
            ]}
          />
        </div>
      </div>

      <div className="px-4 md:px-6 py-8 max-w-4xl mx-auto">
        <article className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full md:w-56">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} - 韩国漫画在线阅读`}
                    title={`${manga.title} - ${manga.category}韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">{manga.title}</h1>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <Link
                    to={`/category/${manga.categorySlug}`}
                    className="px-3 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full cursor-pointer"
                  >
                    {manga.category}
                  </Link>
                  <span className={`px-3 py-0.5 text-xs font-medium rounded-full ${
                    manga.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {manga.status}
                  </span>
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-3 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                  <span className="text-foreground-500">
                    <strong className="text-foreground-700">作者：</strong>{manga.author}
                  </span>
                  <span className="text-foreground-500">
                    <strong className="text-foreground-700">评分：</strong>
                    <span className="text-accent-500 font-semibold">{manga.rating}</span>
                  </span>
                  <span className="text-foreground-500">
                    <strong className="text-foreground-700">更新：</strong>{manga.updatedAt}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                  <span className="text-foreground-500">
                    <strong className="text-foreground-700">阅读量：</strong>
                    <span className="text-primary-500 font-semibold">{(manga.views / 10000).toFixed(0)}万</span>
                  </span>
                  <span className="text-foreground-500">
                    <strong className="text-foreground-700">总话数：</strong>{manga.chapters}话
                  </span>
                </div>

                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
                  <button className="px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    开始阅读第一话
                  </button>
                  <button className="px-8 py-3 border border-background-300 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>

          {manga.content && (
            <div className="border-t border-background-200/70 px-6 md:px-8 py-6 md:py-8">
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">精彩试读</h2>
              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: manga.content }}
              />
            </div>
          )}
        </article>

        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <Link
            to={`/category/${manga.categorySlug}`}
            className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回{manga.category}漫画分类
          </Link>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 hover:bg-background-100 transition-colors cursor-pointer" aria-label="分享到微博">
              <i className="ri-weibo-line text-foreground-600"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 hover:bg-background-100 transition-colors cursor-pointer" aria-label="分享到微信">
              <i className="ri-wechat-line text-foreground-600"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 hover:bg-background-100 transition-colors cursor-pointer" aria-label="复制链接">
              <i className="ri-link text-foreground-600"></i>
            </button>
          </div>
        </div>
      </div>

      {relatedManga.length > 0 && (
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedManga.map((rm) => (
                <MangaCard
                  key={rm.id}
                  id={rm.id}
                  title={rm.title}
                  coverImage={rm.coverImage}
                  category={rm.category}
                  categorySlug={rm.categorySlug}
                  rating={rm.rating}
                  chapters={rm.chapters}
                  updatedAt={rm.updatedAt}
                  status={rm.status}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}